import { useContext, useMemo } from 'react'

import {
  getPropertyMetasearchPriceAccuracyEventData,
  selectMetasearchPriceAccuracyCandidate,
} from 'analytics/metasearchPriceAccuracyTracking'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useFireMetasearchPriceAccuracyEvent from 'hooks/OfferPage/useFireMetasearchPriceAccuracyEvent'
import useMetasearchDiscount from 'hooks/useMetasearchDiscount'
import { useAppSelector } from 'hooks/reduxHooks'
import {
  getPropertyCheapestHotelOnlyRateForDiscount,
  getPropertyMetasearchPriceAccuracyCandidateRates,
  getPropertyMetasearchPriceAccuracyRate,
} from 'selectors/offerPage/offerPageSelectors'

// Fires the SPP `metasearch_price_accuracy` event. Derivation lives in selectors/shared helpers
// (per the `PropertyRoomOptions` "no derivation here" guard); this hook only reads and wires them.
export default function usePropertyMetasearchPriceAccuracyTracking() {
  const offerPageState = useContext(OfferPageStateContext)
  const { checkInDate: checkIn, checkOutDate: checkOut, rooms } = offerPageState

  const ghaPrice = useAppSelector(appState => appState.router.location.query.gha_price)
  const metasearchRoomRateId = useAppSelector(appState => (
    appState.router.location.query.roomRateId || appState.router.location.query.roomOptionRateId
  ))
  const priceTotal = useAppSelector(appState => {
    const parsed = Number(appState.router.location.query.priceTotal)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
  })

  const candidateRates = useAppSelector(appState => getPropertyMetasearchPriceAccuracyCandidateRates(offerPageState, appState))
  const fallbackRate = useAppSelector(appState => getPropertyMetasearchPriceAccuracyRate(offerPageState, appState))
  const discountEligibleRate = useAppSelector(appState => (
    getPropertyCheapestHotelOnlyRateForDiscount(offerPageState, appState)
  ))

  // Fetch the discount for the eligible rate regardless of which rate we end up recording.
  const discountEligibleBedbankPropertyId = discountEligibleRate?.service.type === 'bedbank' ?
    discountEligibleRate.service.propertyId :
    undefined
  const [discountAmount, discountFetching, discountSettled] = useMetasearchDiscount(
    discountEligibleRate?.totals.displayPrice ?? 0,
    {
      propertyId: discountEligibleBedbankPropertyId,
      checkIn,
      checkOut,
      rooms,
      disabled: !discountEligibleBedbankPropertyId,
    },
  )

  const chosen = useMemo(() => selectMetasearchPriceAccuracyCandidate({
    candidates: candidateRates,
    getPrice: rate => rate.totals.displayPrice,
    isDiscountEligible: rate => (
      rate.service.type === 'bedbank' &&
      discountEligibleRate?.service.type === 'bedbank' &&
      discountEligibleRate.service.propertyId === rate.service.propertyId &&
      discountEligibleRate.rateId === rate.rateId
    ),
    discountAmount,
    priceTotal,
    fallbackRate,
  }), [candidateRates, discountAmount, discountEligibleRate, fallbackRate, priceTotal])

  // The page offer id is the bedbank id we join on downstream; used for LPC rows too (LPC rates
  // don't carry it).
  const offerId = offerPageState.offer?.id ?? offerPageState.bedbankOffer?.id
  const eventData = useMemo(() => (
    chosen && offerId ?
        getPropertyMetasearchPriceAccuracyEventData({
          offerId,
          pageType: 'spp',
          metasearchRoomRateId,
          rate: chosen.rate,
          price: chosen.price,
        }) :
      undefined
  ), [chosen, offerId, metasearchRoomRateId])

  useFireMetasearchPriceAccuracyEvent(
    eventData,
    ghaPrice === '1' && !discountFetching && discountSettled,
    `spp:${offerId ?? ''}`,
  )
}
